/**
 * Economic outlook page — nowcast / inflation / recession views per country
 * with release-delta modal. Mounts at both `/economic-outlook` and
 * `/econ-activity`.
 *
 * Page layout:
 *   - Centered title with absolute-right Activity / Inflation toggle.
 *   - Description callout.
 *   - Selectors card: Country | View (or Type) | Period.
 *     - GDP view: View selector exposes "Nowcast" / "Recession".
 *     - Inflation view: Type selector exposes "Core" / "Headline".
 *   - Chart card: shows exactly one of NowcastChart / RecessionChart /
 *     InflationChart (see OutlookChartCard for why it is not display-toggled).
 *   - Releases panel: rendered for the GDP/Nowcast view only.
 *
 * Sibling files own the heavier logic: `useUrlSync` (URL <-> store),
 * `csvExport` (download builders), `OutlookChartCard` (chart slot),
 * `CalloutBoxes` (static prose).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth/useAuth';
import { trackActivity } from '@/lib/analytics/tracker';
import { downloadCsv } from '@/lib/csv';
import { formatLong } from '@/lib/utils/dates';
import { longName } from './lib/countries';
import CountrySelector from './components/CountrySelector';
import ViewModeToggle from './components/ViewModeToggle';
import OutlookChartCard from './components/OutlookChartCard';
import { GdpDescriptionCallout, InflationExplanationBox } from './components/CalloutBoxes';
import SegmentedControl from '@/components/SegmentedControl';
import { GDP_VIEW_OPTIONS, INFLATION_TYPE_OPTIONS, TIMEFRAME_OPTIONS } from './lib/selectorOptions';
import { useCountries, useInflationData, useNowcasts, useRecessionProbabilities } from './api';
import { FALLBACK_DEFAULT_TIMEFRAME, FALLBACK_TIMEFRAMES } from './lib/countries';
import { buildInflationCsv, buildNowcastCsv } from './lib/csvExport';
import { useUrlSync } from './lib/useUrlSync';
import { selectActiveTimeframe, useNowcastsStore } from './store';
import type { Timeframe } from './types';

export default function EconomicOutlookPage(): JSX.Element {
  const { isFulcrumInternal } = useAuth();
  const countriesMap = useCountries().data?.countries;

  const {
    selectedCountry,
    view,
    gdpViewSelector,
    inflationType,
    setSelectedCountry,
    setTimeframe,
    setView,
    setGdpViewSelector,
    setInflationType,
  } = useNowcastsStore();
  // Resolves to gdpTimeframe in GDP view, inflationTimeframe in Inflation
  // view. GDP defaults to 2y, Inflation to 5y.
  const timeframe = useNowcastsStore(selectActiveTimeframe);

  // Keep the URL query string and the store in sync, both directions.
  useUrlSync(timeframe);

  // Per-country timeframe config, with build-time fallbacks.
  const countryConfig = countriesMap?.[selectedCountry];
  const availableTimeframes: Timeframe[] =
    countryConfig?.available_timeframes ?? FALLBACK_TIMEFRAMES;
  const defaultTimeframe: Timeframe =
    countryConfig?.default_timeframe ?? FALLBACK_DEFAULT_TIMEFRAME;
  // Timeframes this country's config does not expose — rendered disabled.
  const disabledTimeframes: Timeframe[] = useMemo(
    () => TIMEFRAME_OPTIONS.map((o) => o.value).filter((tf) => !availableTimeframes.includes(tf)),
    [availableTimeframes],
  );

  // When switching to a country that doesn't support the active timeframe,
  // fall back to that country's default.
  useEffect(() => {
    if (!availableTimeframes.includes(timeframe)) {
      setTimeframe(defaultTimeframe);
    }
  }, [availableTimeframes, timeframe, defaultTimeframe, setTimeframe]);

  // In-app view/country switches are tracked as synthetic page_views
  // (matches the legacy economic-outlook-integrated.js tracking).
  const handleViewChange = useCallback(
    (next: typeof view): void => {
      setView(next);
      trackActivity('page_view', `Econ Outlook: ${next === 'gdp' ? 'Activity' : 'Inflation'}`, {});
    },
    [setView],
  );

  const handleCountryChange = useCallback(
    (next: typeof selectedCountry): void => {
      setSelectedCountry(next);
      const name = longName(next);
      const desc =
        view === 'inflation'
          ? `Econ Outlook: Inflation, ${name}, ${inflationType}`
          : `Econ Outlook: Activity, ${name}, ${gdpViewSelector}`;
      trackActivity('page_view', desc, {});
      trackActivity('country_select', desc, {
        country: next,
        country_name: name,
        surface: 'economic_outlook',
        view,
        inflation_type: inflationType,
      });
    },
    [setSelectedCountry, view, inflationType, gdpViewSelector],
  );

  const handleGdpViewSelectorChange = useCallback(
    (next: typeof gdpViewSelector): void => {
      setGdpViewSelector(next);
      trackActivity(
        'page_view',
        `Econ Outlook: Activity, ${longName(selectedCountry)}, ${next}`,
        {},
      );
    },
    [setGdpViewSelector, selectedCountry],
  );

  const handleInflationTypeChange = useCallback(
    (next: typeof inflationType): void => {
      setInflationType(next);
      trackActivity(
        'page_view',
        `Econ Outlook: Inflation, ${longName(selectedCountry)}, ${next}`,
        {},
      );
    },
    [setInflationType, selectedCountry],
  );

  const isRecession = view === 'gdp' && gdpViewSelector === 'Recession';
  const isInflation = view === 'inflation';
  const isGdp = view === 'gdp';
  const activeChart = isInflation ? 'inflation' : isRecession ? 'recession' : 'nowcast';

  // Server-state queries. Inflation only fires in inflation view, but BOTH
  // nowcast and recession fire whenever GDP view is active — OutlookChartCard
  // mounts one at a time but the selector flips fast, so keep both warm.
  const nowcastQuery = useNowcasts(isGdp ? selectedCountry : null, timeframe);
  const recessionQuery = useRecessionProbabilities(isGdp ? selectedCountry : null, timeframe);
  const inflationQuery = useInflationData(
    isInflation ? selectedCountry : null,
    inflationType,
    timeframe,
  );

  // Chart point-click no longer opens the release-delta modal (disabled).
  const handlePointClick = (): void => {};

  const [downloadError, setDownloadError] = useState<string | null>(null);

  /** Download the active view's series as CSV. Superuser-gated. */
  const handleDownload = async (): Promise<void> => {
    setDownloadError(null);
    const file = isInflation
      ? await buildInflationCsv(selectedCountry, inflationType, timeframe)
      : await buildNowcastCsv(selectedCountry, timeframe);
    if (!file) {
      setDownloadError('No data available for download');
      return;
    }
    downloadCsv(file.text, file.filename);
  };

  const titleText = isInflation
    ? 'Inflation Forecasts'
    : 'Real Time Estimates of Underlying Activity';

  return (
    <>
      <div className="forecasts-section">
        <div className="mx-auto max-w-[1280px] px-4 py-4">
          {/* Title with absolute-right toggle. 28px heading; matches the
              rest of the dashboard h2s. */}
          <div className="relative">
            <h2 className="mb-2 text-center font-[var(--font-heading)] text-[28px] font-bold leading-8 text-gray-800">
              {titleText}
            </h2>
            <ViewModeToggle value={view} onChange={handleViewChange} />
          </div>
          <p className="mb-6 text-center text-sm text-gray-500">As of {formatLong(new Date())}</p>

          {view === 'gdp' && <GdpDescriptionCallout />}

          {/* Selector controls card */}
          <section
            className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            aria-label="Selectors"
          >
            <div className="flex flex-col gap-6 md:flex-row md:flex-wrap md:items-center md:justify-between">
              <CountrySelector
                value={selectedCountry}
                onChange={handleCountryChange}
                countries={countriesMap ?? null}
              />
              {view === 'gdp' ? (
                <SegmentedControl
                  options={GDP_VIEW_OPTIONS}
                  value={gdpViewSelector}
                  onChange={handleGdpViewSelectorChange}
                  label="View:"
                />
              ) : (
                <SegmentedControl
                  options={INFLATION_TYPE_OPTIONS}
                  value={inflationType}
                  onChange={handleInflationTypeChange}
                  label="Type:"
                />
              )}
              <SegmentedControl
                options={TIMEFRAME_OPTIONS}
                value={timeframe}
                onChange={setTimeframe}
                label="Period:"
                disabledValues={disabledTimeframes}
                dense
              />
              {/* Fulcrum-internal only; hidden in the Recession sub-view. */}
              {isFulcrumInternal && !isRecession && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex items-center gap-2 rounded-lg bg-[color:var(--accent)] p-2.5 text-white shadow-sm transition-colors hover:bg-[color:var(--accent-hover)]"
                  title="Download data as CSV"
                  aria-label="Download CSV"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                  </svg>
                </button>
              )}
              {downloadError && (
                <span role="alert" className="text-xs text-red-600">
                  {downloadError}
                </span>
              )}
            </div>
          </section>

          {/* Chart card */}
          <div className="mb-4">
            <div className="my-6">
              <OutlookChartCard
                country={selectedCountry}
                active={activeChart}
                inflationType={inflationType}
                nowcastQuery={nowcastQuery}
                recessionQuery={recessionQuery}
                inflationQuery={inflationQuery}
                onPointClick={handlePointClick}
              />
            </div>
          </div>

          {isInflation && <InflationExplanationBox />}
        </div>
      </div>
    </>
  );
}
